// Pure progress-derivation math for the "Projetos" delivery module
// (src/hooks/useDeliveryProjects.ts). Extracted into src/lib/ — per this
// repo's convention that unit tests cover pure functions here — because
// these are the only new pure logic in Task 7a and there are no component
// tests to catch a regression in them.

export type ProjectPhase = "backlog" | "in_progress" | "review" | "done";

export interface BacklogItemLike {
  id: string;
  status: string;
}

export interface SubtaskLike {
  backlog_item_id: string;
  is_done: boolean;
}

const PHASE_ORDER: ProjectPhase[] = ["backlog", "in_progress", "review", "done"];

/**
 * Derives 0–100 progress for a Jira-mode project from its backlog items and
 * their subtasks. A backlog item WITH subtasks counts each subtask as one
 * unit (finer granularity); a backlog item with no subtasks counts as one
 * unit itself, done iff status === "done". Returns null when the project has
 * no backlog items at all (nothing to compute from — show "—", not 0%).
 */
export function jiraProgress(
  items: BacklogItemLike[],
  subtasksByItem: Map<string, SubtaskLike[]>,
): number | null {
  if (items.length === 0) return null;
  let totalUnits = 0;
  let doneUnits = 0;
  for (const item of items) {
    const subs = subtasksByItem.get(item.id) ?? [];
    if (subs.length > 0) {
      totalUnits += subs.length;
      doneUnits += subs.filter((s) => s.is_done).length;
    } else {
      totalUnits += 1;
      if (item.status === "done") doneUnits += 1;
    }
  }
  return totalUnits === 0 ? null : Math.round((doneUnits / totalUnits) * 100);
}

/**
 * Legacy (pre-Jira-mode) fallback: the 4-phase kanban has no finer-grained
 * data, so progress is the phase's position in the fixed pipeline
 * (backlog=0%, in_progress=33%, review=67%, done=100%). This is a real,
 * admin-set field — not invented — but it's coarse by nature; that's
 * inherent to the legacy model, not a bug in this derivation. Callers must
 * label a value derived this way as an estimate (see `progressBasis` on
 * `DeliveryProject`) rather than presenting it as measured the same way a
 * Jira-mode percentage is.
 */
export function phaseProgress(phase: ProjectPhase | null): number | null {
  if (!phase) return null;
  const idx = PHASE_ORDER.indexOf(phase);
  if (idx === -1) return null;
  return Math.round((idx / (PHASE_ORDER.length - 1)) * 100);
}
